import { call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';
import { push, replace } from 'redux-first-history';
import { stringify } from 'query-string';
import { map } from 'lodash-es';
import { ContactsList } from '@dev/ee-api/ee-api-models';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import React from 'react';
import * as Types from '@/store/lists/types';
import getSafeRuleListName from '@/services/tools/getSafeRuleListName/getSafeRuleListName';
import {
    getAllContactsLists,
    getContactListsNames,
    getListsCount,
    ListAddContactsError,
    ListAddContactsSuccess,
    ListAddError,
    ListAddSuccess,
    ListCreateNthError,
    ListCreateNthSuccess,
    ListCreateRandomError,
    ListCreateRandomSuccess,
    ListDeleteError,
    ListEditError,
    ListEditSuccess,
    ListFromCampaignError,
    ListFromCampaignSuccess,
    ListLoadByContactError,
    ListLoadByContactSuccess,
    ListLoadSingleError,
    ListLoadSingleSuccess,
    ListMoveContactsError,
    ListMoveContactsSuccess,
    ListRemoveContactsError,
    ListRemoveContactsSuccess,
    ListSetParams,
    ListsGetNamesError,
    ListsGetNamesSuccess,
    ListsLoadError,
    ListsLoadHistoryTrackedErrorAction,
    ListsLoadHistoryTrackedSuccessAction,
    ListsLoadStart,
    ListsLoadSuccess,
} from '@/store/lists/actions';
import {
    addContactsToList,
    addList,
    createNthSelection,
    createRandomList,
    deleteList,
    listFromCampaign,
    loadContactLists,
    loadLists,
    loadListsHistory,
    loadSingleList,
    moveContactsToList,
    removeContactsFromList,
    updateList,
} from '@/services/ElasticApi';
import { HideToast, ShowToast } from '@/store/toast/actions';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { ContactsStartLoading } from '@/store/contacts/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { calculateOffset } from '@/services/tools/handleUrlParams';
import { headersParser } from '@/services/tools/headersParser';

import i18n from '@/../i18n';
import { ShowDialog } from '@/store/dialog/actions';
import { DeletionNotAllowedDialog } from '@/services/Dialog/DeletionNotAllowedDialog';
import { matchErrorMessagesForUsageInFeatures } from '@/services/tools/matchErrorMessages';
import { Logger } from '@/services/errors/appLogger';
function* getSingleContactList(action): any {
    try {
        const list = yield call(loadSingleList, action.payload.listname);
        yield put(ListLoadSingleSuccess(list));
    } catch (e) {
        yield put(ListLoadSingleError());
        Logger.error(e);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchGetSingleContactsList(): any {
    yield takeLatest('@@lists/SINGLE_FETCH_START', getSingleContactList);
}

function* getLists(action): any {
    try {
        const {
            from,
            to,
            limit = DEFAULT_PER_PAGE_COUNT,
            offset = 0,
            includeHistory,
            search,
            modifyQueryString,
            getAllElements,
        } = action.payload;
        const params = {
            includeHistory,
            limit,
            offset,
            from,
            to,
            search,
            getAllElements,
        };

        let loadListsResponse;

        if (getAllElements) {
            loadListsResponse = yield call(loadLists, { includeHistory });
        } else {
            loadListsResponse = yield call(loadLists, params);
        }

        const { total, trackedlist } = headersParser(loadListsResponse.headers);
        const lists = loadListsResponse.data;

        const totalCount = +(total?.count || 0);
        const totalTrackedLists = +(trackedlist?.count || 0);

        const activePage = (offset + limit) / limit;
        if (modifyQueryString) {
            yield put(replace({ search: stringify({ perPage: limit, page: activePage, search }) }));
        }

        yield put(ListsLoadSuccess(lists, totalCount, totalTrackedLists));
    } catch (e) {
        yield put(ListsLoadError());
        Logger.error(e);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchGetLists(): any {
    yield takeLatest('@@lists/FETCH_START', getLists);
}

function* getListNames(): any {
    try {
        const params = {
            includeHistory: false,
            limit: 0,
            offset: 0,
        };
        const loadListsResponse = yield call(loadLists, params);
        const lists = loadListsResponse.data;

        yield put(ListsGetNamesSuccess(lists));
    } catch (e) {
        yield put(ListsGetNamesError());
    }
}

function* watchGetListNames(): any {
    yield takeLatest('@@lists/FETCH_LISTNAMES_START', getListNames);
}

function* addRandomList(action): any {
    try {
        const list = yield call(createRandomList, action.payload.list);
        yield put(ListCreateRandomSuccess(list));
        yield put(replace(ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(list.ListName))));
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('listcreated'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListCreateRandomError());
        Logger.error(e);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchAddRandomList(): any {
    yield takeLatest('@@lists/CREATE_RANDOM_START', addRandomList);
}

function* addNthList({ payload }: Types.ListCreateNthStartAction): any {
    try {
        yield call(createNthSelection, payload?.list);
        yield put(ListCreateNthSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('listscreated'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListCreateNthError());
        Logger.error(e);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchAddNthList(): any {
    yield takeLatest('@@lists/CREATE_NTH_START', addNthList);
}

//create action
function* setRandomListParams(action): any {
    try {
        //put data to store after delay
        yield delay(1000);
        yield put(ListSetParams(action.payload.newList));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
//create watcher
function* watchSetRandomListParams(): any {
    yield takeLatest('@@lists/CREATE_NEW_PARAMS', setRandomListParams);
}

function* deleteSingleList(action): any {
    try {
        const { name, perPage, activePage, search } = action.payload;
        const count = yield select(getListsCount);
        const offset = activePage ? calculateOffset(activePage, perPage, count - 1) : 0;
        yield call(deleteList, name);

        yield put(push(ContactsPaths.Lists));

        yield put(ListsLoadStart({ limit: perPage, offset, search }));
        yield put(
            ShowToast({
                severity: 'info',
                message: `${i18n.t('listdeleted')}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        Logger.error(e);
        yield put(ListDeleteError(action.meta));
        const errorParams = e?.response?.data?.ErrorParams;

        const matchError = matchErrorMessagesForUsageInFeatures(errorParams);

        if (matchError?.length) {
            yield put(
                ShowDialog({
                    title: i18n.t('youcannotdeletethislist'),
                    cancelLabel: i18n.t('close'),
                    body: <DeletionNotAllowedDialog type="list" list={matchError} />,
                })
            );
            return;
        }

        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchDeleteList(): any {
    yield takeLatest('@@lists/DELETE_START', deleteSingleList);
}

function* createList(action): any {
    const { payload, meta } = action;
    try {
        const { data: newList } = yield call(addList, payload.list);

        yield put(ListAddSuccess(meta));
        const currentListNames = yield select(getContactListsNames);
        if (currentListNames) {
            yield put(ListsGetNamesSuccess([...currentListNames, newList]));
        }
        yield delay(500);

        yield put(replace(ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(payload.list.ListName))));
        yield put(
            ShowToast({
                severity: 'info',
                message: `${action.payload.isCopy ? i18n.t('listcopied') : i18n.t('listcreated')}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListAddError(meta));
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchCreateList(): any {
    yield takeLatest('@@lists/ADD_START', createList);
}

function* editList(action: Types.ListEditStartAction): any {
    try {
        const updatedSingleList: ContactsList = yield call(updateList, action.payload.listname, action.payload.body);
        const currentCollection: ContactsList[] = yield select(getAllContactsLists);

        const updatedLists = map(currentCollection, (currentSingleList) =>
            currentSingleList.ListName === updatedSingleList.ListName ? updatedSingleList : currentSingleList
        );
        yield put(ListEditSuccess(updatedLists));

        yield put(
            replace(
                ContactsPaths.SingleList.replace(
                    ':listname',
                    safelyEncodeBase64Url(action.payload.body.NewListName || action.payload.listname)
                )
            )
        );
        yield put(
            ShowToast({
                severity: 'info',
                message: `${i18n.t('listsaved')}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListEditError());
        yield put(
            ShowToast({
                severity: 'error',
                message: e?.response?.data?.Error,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchEditList(): any {
    yield takeLatest('@@lists/EDIT_START', editList);
}

function* loadHistoryTrackedLists(action: Types.ListsLoadHistoryTrackedStart): any {
    try {
        const { includeHistory = true, from, to } = action.payload;

        const historyTrackedSegments = yield call(loadListsHistory, { includeHistory, from, to });

        yield put(ListsLoadHistoryTrackedSuccessAction(historyTrackedSegments.data));
    } catch (e) {
        yield put(ListsLoadHistoryTrackedErrorAction());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadHistoryTrackedLists(): any {
    yield takeLatest('@@lists/LOAD_HISTORY_TRACKED_START', loadHistoryTrackedLists);
}

function* addToList(action): any {
    try {
        const { listName, body } = action.payload;

        yield call(addContactsToList, listName, body);
        yield put(ListAddContactsSuccess());
        yield put(replace(ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(listName))));
        const list = yield call(loadSingleList, listName);
        yield put(ListLoadSingleSuccess(list));

        yield put(
            ContactsStartLoading({
                rule: `List = '${getSafeRuleListName(listName)}'`,
                limit: DEFAULT_PER_PAGE_COUNT,
                offset: 0,
            })
        );
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsadded'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        console.log(e);
        yield put(ListAddContactsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchAddContactsToList(): any {
    yield takeLatest('@@lists/ADD_CONTACTS_START', addToList);
}

function* removeFromList(action): any {
    try {
        const { limit = DEFAULT_PER_PAGE_COUNT, offset = 0, listName, body, statuses } = action.payload;
        yield call(removeContactsFromList, listName, body);
        const contactsDescription = body.Emails?.length === 1 ? i18n.t('contact') : i18n.t('contacts');
        const safeListName = getSafeRuleListName(listName);
        yield delay(1000);
        yield put(ListRemoveContactsSuccess());
        yield put(ContactsStartLoading({ rule: `List = '${safeListName}'`, limit, offset, statuses }));
        yield put(
            ShowToast({
                severity: 'info',
                message: `${contactsDescription} ${i18n.t('removed').toLowerCase()}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListRemoveContactsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchRemoveContactsFromList(): any {
    yield takeLatest('@@lists/REMOVE_CONTACTS_START', removeFromList);
}

function* moveToList(action): any {
    try {
        yield call(moveContactsToList, action.payload.listName, action.payload.body);
        yield put(ListMoveContactsSuccess());
        yield put(
            replace(
                ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(action.payload.body.newListName))
            )
        );
        const list = yield call(loadSingleList, action.payload.body.newListName);
        yield put(ListLoadSingleSuccess(list));
        yield put(
            ContactsStartLoading({
                rule: `List = '${getSafeRuleListName(action.payload.body.newListName)}'`,
                limit: DEFAULT_PER_PAGE_COUNT,
                offset: 0,
            })
        );
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsmoved'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListMoveContactsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchMoveContactsToList(): any {
    yield takeLatest('@@lists/MOVE_CONTACTS_START', moveToList);
}

function* getContactLists(action: Types.ListLoadByContactStartAction): any {
    try {
        const contactLists = yield call(loadContactLists, action.payload.email);

        yield put(ListLoadByContactSuccess(contactLists));
    } catch (e) {
        yield put(ListLoadByContactError());
        if (action.payload.showErrorToast) {
            yield put(
                ShowToast({
                    severity: 'error',
                    message: `Error: ${e?.response?.data?.Error}`,
                    duration: 6000,
                    onClose: HideToast,
                })
            );
        }
    }
}

function* watchGetContactLists(): any {
    yield takeLatest('@@lists/LOAD_BY_CONTACT_START', getContactLists);
}

function* createListFromCampaign(action: Types.ListFromCampaignStartAction): any {
    try {
        yield call(listFromCampaign, action.payload.params);

        yield put(ListFromCampaignSuccess());

        const list = yield call(loadSingleList, action.payload.params.listName);
        yield put(ListLoadSingleSuccess(list));

        yield put(
            replace(
                ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(action.payload.params.listName))
            )
        );

        yield put(
            ContactsStartLoading({
                rule: `List = '${action.payload.params.listName}'`,
                limit: DEFAULT_PER_PAGE_COUNT,
                offset: 0,
            })
        );
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsadded'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ListFromCampaignError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchCreateListFromCampaign(): any {
    yield takeLatest('@@lists/LIST_FROM_CAMPAIGN_START', createListFromCampaign);
}

const listSagas = [
    fork(watchGetLists),
    fork(watchGetContactLists),
    fork(watchAddRandomList),
    fork(watchAddNthList),
    fork(watchSetRandomListParams),
    fork(watchDeleteList),
    fork(watchCreateList),
    fork(watchEditList),
    fork(watchGetSingleContactsList),
    fork(watchLoadHistoryTrackedLists),
    fork(watchAddContactsToList),
    fork(watchRemoveContactsFromList),
    fork(watchMoveContactsToList),
    fork(watchGetListNames),
    fork(watchCreateListFromCampaign),
];

export default listSagas;
